import { memo, useCallback, useMemo, useState } from 'react';

import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import CastAndCrewSelectWithInvites from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/cast-and-crew/cast-and-crew-select-with-invites/cast-and-crew-select-with-invites';
import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import useIsModalOpen from '@ui/modal/use-is-modal-open';
import { useCastAndCrewContext } from '../../cast-and-crew-context/use-cast-and-crew-context';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import saveUpdatedCast from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/cast-and-crew/helpers/save-updated-cast';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSessionByUuid } from '@/store/slices/project/selectors';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import useSpeakersForSession from '@/utils/speakers/use-speakers-for-session';
import { difference } from 'underscore';
// see implentation comment below
// import InviteAllSelectedCast from './invite-all-selected-cast';

type Props = {
  sessionUuid?: string;
};

const AddCastModal = memo(({ sessionUuid }: Props) => {
  const [crewSelectActive, setCrewSelectActive] = useState(false);
  const [hasFinishedOpening, setHasFinishedOpening] = useState(false);
  const [workingSpeakers, setWorkingSpeakers] = useState<TReactSelectOption[]>([]);
  const [savingSpeakers, setSavingSpeakers] = useState(false);

  const session = useAppSelector(selectSessionByUuid(sessionUuid));

  const sessionSpeakers = useSpeakersForSession({ sessionUuid });

  const dispatch = useAppDispatch();

  const {
    state: { addCastModalOpen },
    dispatch: contextDispatch,
  } = useCastAndCrewContext();

  const handleCrewSelectActive = useCallback((isActive: boolean) => {
    if (!isActive) {
      /*
        Without the timeout, the component state updates too fast and thinks that the state
        was closed before the onRequestClose event fires. It's just a side effect of the
        way custom-select handles click events.
      */
      setTimeout(() => {
        setCrewSelectActive(false);
      }, 100);
    } else {
      setCrewSelectActive(true);
    }
  }, []);

  const isInviteAllCastModalOpen = useIsModalOpen('invite-all-selected-cast-modal');
  const isInviteCastMemberModalOpen = useIsModalOpen('invite-cast-member-modal');

  const handleCloseModal = useCallback(
    (options?: { forceClose?: boolean }) => {
      if (!options?.forceClose && (crewSelectActive || isInviteAllCastModalOpen || isInviteCastMemberModalOpen)) {
        return;
      }
      contextDispatch({ type: 'TOGGLE_ADD_CAST_AND_CREW_OPEN', payload: false });
    },
    [contextDispatch, crewSelectActive, isInviteAllCastModalOpen, isInviteCastMemberModalOpen]
  );

  const handleTransitionOpened = useCallback(() => setHasFinishedOpening(true), []);
  const handleTransitionClosed = useCallback(() => setHasFinishedOpening(false), []);

  const handleSaveWorkingSpeakers = async () => {
    try {
      if (!session) return;
      setSavingSpeakers(true);
      await saveUpdatedCast({
        session,
        selectedOptions: workingSpeakers,
        dispatch,
      });

      handleCloseModal({ forceClose: true });
    } catch (error) {
      console.error(error);
    } finally {
      setSavingSpeakers(false);
    }
  };

  const selectionHasDiff = useMemo(() => {
    const workingSpeakerIds = workingSpeakers.map((s) => s.value);
    const sessionSpeakerIds = sessionSpeakers.map((s) => s.speaker);
    return !!difference(workingSpeakerIds, sessionSpeakerIds).length;
  }, [sessionSpeakers, workingSpeakers]);

  if (!sessionUuid) return null;
  return (
    <Modal
      design="GlobalCont1"
      id="add-cast-modal"
      customSize={{ height: 'fit-content', minHeight: '192px', width: '640px', maxWidth: '90vw' }}
      open={addCastModalOpen}
      onRequestClose={handleCloseModal}
      onTransitionOpened={handleTransitionOpened}
      onTransitionClosed={handleTransitionClosed}
      renderHeaderContent={() => (
        <Container height="96px" padding="24px 24px 16px 40px" className="flex-column gap-4">
          <Container className="flex ai-center jc-space-between">
            <Text design="GlobalIcoText1" className="H4" tag="h4">
              Add cast members
            </Text>
            <Button
              designSize="small"
              design="LocalBttnSec"
              className="radius-full square-32 flex-column ai-center jc-center"
              // this button intentionally bypasses the handleCloseModal conditional check
              onClick={() => {
                contextDispatch({ type: 'TOGGLE_ADD_CAST_AND_CREW_OPEN', payload: false });
              }}
            >
              <Icon name={ICONS.CLOSE} size={10} />
            </Button>
          </Container>
          <Text className="S3" design="GlobalIcoText2">
            Add cast members to your project
          </Text>
        </Container>
      )}
    >
      <Container className="flex ai-center jc-center gap-12" padding="16px 40px 40px 40px">
        <Container width="100%" maxWidth="450px" className="flex-1">
          <CastAndCrewSelectWithInvites
            /* wait for modal to be completely open before opening select to prevent jank */
            hidden={!hasFinishedOpening}
            hideSelectHeader
            sessionUuid={sessionUuid}
            setCrewSelectActive={handleCrewSelectActive}
            autoFocus={addCastModalOpen && hasFinishedOpening}
            maskOverflowColor="var(--global-cont-1)"
            onChange={setWorkingSpeakers}
            onFocusClosed={setWorkingSpeakers}
            hideDefaultOptionsFromSelectedList
          />
        </Container>

        <Button
          backgroundColor={getThemeColorCSSVariable()}
          hoverBackgroundColor={getThemeColorsCssMixVariable()}
          disabledOpacity={0.7}
          designSize="medium"
          onClick={handleSaveWorkingSpeakers}
          loading={savingSpeakers}
          disabled={savingSpeakers || !selectionHasDiff}
        >
          <Text className="flex ai-center gap-8" color={getThemeColorContrastCSSVariable()}>
            <Icon name={ICONS.SEND_3} size={12} />
            <Text whiteSpace="nowrap" className="L1">
              Add
            </Text>
          </Text>
        </Button>

        {/*
          Commenting out but not deleting. Product's been vacillating on this,
          so not gonna delete, just gonna comment out for quick re-introduction.
        */}
        {/* <InviteAllSelectedCast /> */}
      </Container>
    </Modal>
  );
});

AddCastModal.displayName = 'AddCastModal';

export default AddCastModal;
